import { useEffect, useState } from 'react';
import {
  Text,
  Badge,
  Table,
  Group,
  TextInput,
  ActionIcon,
  Anchor,
  ScrollArea,
  Button,
  CloseButton,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import { IconSearch, IconEdit } from '@tabler/icons-react';
import { useQuery } from '@apollo/client';
import { TripProfile } from '@generated/graphql';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';
import { GET_TRIP_PROFILES } from '@queries/trip-profiles';
import ItineraryPreviewModal from './print-trip-modal';

const statusColors: Record<string, string> = {
  current: 'green',
  open: 'blue',
  active: 'cyan',
  pending: 'orange',
  locked: 'red',
};

interface ClientsTableProps {
  clientId: string;
}

function ClientsTable({ clientId }: ClientsTableProps) {
  const [previewOpen, { open: openPreview, close: closePreview }] = useDisclosure(false);
  const [selectedTripProfile, setSelectedTripProfile] = useState<TripProfile | null>(null);
  const [filter, setFilter] = useState('');
  const { loading, error, data, refetch } = useQuery(GET_TRIP_PROFILES, {
    variables: {
      clientId,
    },
    // TODO temp as we reload data and such.
    pollInterval: 900000, // 15min
  });
  useEffect(() => {
    // TODO: work around for the mutation refetchQueries not working outside of local-dev

    void refetch();
  });
  if (loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;

  const filteredData = data?.clientTripProfiles?.filter(
    (profile: TripProfile) =>
      profile.title.toLowerCase().includes(filter.toLowerCase()) ||
      profile.destination?.toLowerCase().includes(filter.toLowerCase())
  );

  const clearSearch = () => setFilter('');

  interface RowProps {
    travelProfiles: TripProfile[] | undefined;
  }

  function Rows({ travelProfiles }: RowProps) {
    if (travelProfiles) {
      const rows = travelProfiles.map((item) => (
        <Table.Tr key={item.id}>
          <Table.Td>
            <Anchor component={Link} to={`trip-profiles/${item.id}`} fw={500} underline="never" inherit={false}>
              {item.title}
            </Anchor>
          </Table.Td>
          <Table.Td>
            <Badge color={statusColors[item.status ? item.status.toLowerCase() : '']} variant="light">
              {item.status}
            </Badge>
          </Table.Td>
          <Table.Td>
            <Text>{item.startDate ? new Date(item.startDate).toLocaleDateString() : '-'}</Text>
          </Table.Td>
          <Table.Td>
            <Text>{item.endDate ? new Date(item.endDate).toLocaleDateString() : '-'}</Text>
          </Table.Td>
          <Table.Td>
            <Text>{item.destination ? `${item.destination}, ${item.country || ''}` : ''}</Text>
          </Table.Td>
          <Table.Td>
            <Anchor
              onClick={() => {
                setSelectedTripProfile(item);
                openPreview();
              }}
            >
              Trip Summary
            </Anchor>
          </Table.Td>
          <Table.Td>
            <Group gap={10} justify="right">
              <Tooltip label="Edit" withArrow>
                <ActionIcon variant="transparent" component={Link} to={`trip-profiles/${item.id}/edit`}>
                  <IconEdit size="1.5rem" />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Table.Td>
        </Table.Tr>
      ));
      return rows;
    }
  }

  return (
    <div>
      <Group justify="space-between" py={25}>
        <TextInput
          w={300}
          leftSection={<IconSearch />}
          placeholder="Trip Profile Search"
          onChange={(e) => setFilter(e.currentTarget.value)}
          rightSection={filter && <CloseButton onClick={clearSearch} />}
        />
        <Button component={Link} to="trip-profiles/new">
          + NEW TRIP
        </Button>
      </Group>

      <ScrollArea>
        <Table verticalSpacing="md" miw={800} stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Trip Theme</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Begin</Table.Th>
              <Table.Th>End</Table.Th>
              <Table.Th>Destination</Table.Th>
              <Table.Th>Overview</Table.Th>
              <Table.Th>&nbsp;</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Rows travelProfiles={filteredData} />
          </Table.Tbody>
        </Table>
      </ScrollArea>
      <ItineraryPreviewModal
        opened={previewOpen}
        onClose={closePreview}
        tripProfile={selectedTripProfile}
        pdfFilename={`Trip Summary-${selectedTripProfile?.title}`}
      />
      {/* <Code block>{JSON.stringify(data, null, 2)}</Code> */}
    </div>
  );
}

export default ClientsTable;
